import { AudioTrack, useLocalParticipant } from "@livekit/components-react";
import { Track } from "livekit-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLiveSessionStore } from "../../../../stores/useLiveSessionStore";


export const LiveKitRoomAudio = () => {
  // debugger;
  const isMicOn = useLiveSessionStore((state) => state.isMicOn);
  const { localParticipant } = useLocalParticipant();
  const [isMicWorking, setIsMicWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Enhanced monitoring (inspired by old implementation)
  const micVolumeFails = useRef<number>(0);
  const micVolumeListener = useRef<NodeJS.Timeout | null>(null);

  // Get the microphone track publication
  const microphonePublication = useMemo(
    () => localParticipant.getTrackPublication(Track.Source.Microphone),
    [localParticipant]
  );

  useEffect(() => {
    handleMicrophoneControl().catch(console.error);

    // Cleanup function
    return () => {
      if (micVolumeListener.current) {
        clearInterval(micVolumeListener.current);
        micVolumeListener.current = null;
      }
    };
  }, [isMicOn, localParticipant]);

  const handleMicrophoneControl = async () => {
    try {
      setError(null);

      if (isMicOn) {
        // Enable microphone and acquire track if needed
        // First, try to enable the microphone
        await localParticipant.setMicrophoneEnabled(true);

        // If no track exists, we need to acquire one
        if (!microphonePublication || !microphonePublication.track) {
          try {
            // This will prompt user for microphone permission if not already granted
            await localParticipant.createTracks({
              audio: true,
              video: false,
            });
          } catch (trackError) {
            console.error(
              "[LiveKitRoomAudio] Failed to acquire microphone track:",
              trackError
            );
            setError("Failed to access microphone. Please check permissions.");
            setIsMicWorking(false);
            return;
          }
        }

        // Verify the microphone is working
        const updatedPublication = localParticipant.getTrackPublication(
          Track.Source.Microphone
        );
        if (updatedPublication && updatedPublication.track) {
          setIsMicWorking(true);

          // Start volume monitoring (inspired by old implementation)
          if (micVolumeListener.current) {
            clearInterval(micVolumeListener.current);
          }

          micVolumeListener.current = setInterval(() => {
            const micAudioLevel = localParticipant.audioLevel ?? 0;

            if (micAudioLevel > 0) {
              micVolumeFails.current = 0;
            } else {
              micVolumeFails.current += 1;

              // If no volume for 60 seconds (120 * 500ms), consider mic disconnected
              if (micVolumeFails.current >= 120) {
                console.warn(
                  "[LiveKitRoomAudio] Microphone appears disconnected"
                );
                setError("Microphone not detecting audio input");
                setIsMicWorking(false);
              }
            }
          }, 500);

          // Notify other participants about microphone status
          localParticipant.publishTrack(updatedPublication.track);
        } else {
          setError("Microphone track not available");
          setIsMicWorking(false);
        }
      } else {
        // Disable microphone
        await localParticipant.setMicrophoneEnabled(false);
        setIsMicWorking(false);

        // Stop volume monitoring
        if (micVolumeListener.current) {
          clearInterval(micVolumeListener.current);
          micVolumeListener.current = null;
        }

        // Unpublish track if it exists
        if (microphonePublication && microphonePublication.track) {
          await localParticipant.unpublishTrack(microphonePublication.track);
        }
      }
    } catch (error) {
      console.error("[LiveKitRoomAudio] Error controlling microphone:", error);
      setError("Error controlling microphone");
      setIsMicWorking(false);

      // Clean up volume monitoring on error
      if (micVolumeListener.current) {
        clearInterval(micVolumeListener.current);
        micVolumeListener.current = null;
      }
    }
  };

  // Show error state if microphone failed
  if (error) {
    // Error state is handled by the error variable
  }

  // Only render AudioTrack when microphone is on and working
  if (!isMicOn || !microphonePublication || !isMicWorking) {
    return null;
  }

  return (
    <AudioTrack
      trackRef={{
        participant: localParticipant,
        source: Track.Source.Microphone,
        publication: microphonePublication,
      }}
      onError={(error) => {
        console.error("[LiveKitRoomAudio] AudioTrack error:", error);
        setError("Audio track error");
        setIsMicWorking(false);
      }}
    />
  );
};
